import Cookies from "js-cookie"
import { API, handleError, vueDomain } from '@/helpers/axios'
import {eventHub} from "@/store/eventHub";
import { UserSession } from '@/store/auth/types'
import wsClient from '@/helpers/wsClient'
import i18next from 'i18next'
import resetStore from '@/helpers/vuexReset'
import store from '@/store'
import {fc3MainHostname} from "@/helpers/constants";

const authAutoCheckTimeout = 30*60*1000 // 30 minutes (in ms)

export default {
  async init(): Promise<UserSession | null> {
    const check = await this.check()
    if(check && check.user) {
      this.setupAutocheck()
      return await this.websocketSetup(check)
    }
    return null
  },
  setupAutocheck(): void {
    const checkTimeout = store.getters['auth/checkTimeout']
    if(checkTimeout) {
      clearTimeout(checkTimeout)
    }
    const newTimeout = setTimeout( () => {
      this.refresh()
    }, authAutoCheckTimeout)
    store.commit('auth/checkTimeout', newTimeout)
  },
  async websocketSetup(check: UserSession): Promise<UserSession | null> {
    if(!check.user) return null // For typescript
    const wssAuthResp = await wsClient.wssAuth()
    if(wssAuthResp.data.status !== 'authenticated') console.error('wssAuth not authenticated')
    wsClient.initialize(check.user.id);
    if(i18next.language !== check.user.language) {
      i18next.changeLanguage(check.user.language)
    }
    return check
  },
  async refresh(): Promise<UserSession | null> {
    const check = await this.check()
    if(check && check.user) {
      this.setupAutocheck()
      return check
    } else {
      this.loginRedirect()
      return null
    }
  },
  async check(): Promise<UserSession | null> {
    try {
      const authcheck = await API.get('api/user-session')
      if((authcheck.status === 200 && authcheck.data.wss)
        || this.httpCodeSwitch(authcheck.status)) {
        return authcheck.data
      }
      else {
        console.debug('request failed authcheck!', authcheck)
        handleError({ response: {
                          status: authcheck.status,
                          config: {
                            baseURL: vueDomain(),
                            url: ' '
                          }
                        },
                        config: {
                                      baseURL: vueDomain(),
                                      url: ' '
                        }})
        return null
      }

    } catch (e) {
      // unauthenticated users wind up here
      if(e.response.status === 401 && e.response.data.errorMessage === 'Missing authentication') {
        eventHub.$emit('triggerSnackbar', {
          color: 'warning',
          text: i18next.t('It looks like you are not logged in. Redirecting you to login in 4 seconds.'),
          timeout: 4000,
          undo: this.loginRedirect,
          undoText: i18next.t('Go to login form')
        })
        setTimeout(() => { this.loginRedirect() }, 4000)

      } else if(e.response.status === 403 && e.response.data.errorMessage === 'Insufficient scope') {
        eventHub.$emit('triggerSnackbar', {
          color: 'warning',
          text: i18next.t('You do not have access to the requested page. Redirecting you in 3 seconds.'),
          timeout: 3000,
          undo: this.loggedInRedirect,
          undoText: i18next.t('Redirect now')
        })
        setTimeout(() => { this.loggedInRedirect() }, 3000)
      } else {
        resetStore()
        handleError(e)
      }
      return null
    }
  },
  httpCodeSwitch(code: number) : void | boolean {
    switch (code) {
      case 401: // User is unauthenticated
        console.log('redirecting for 401')
        this.loginRedirect()
        break
      case 403: // Unauthorized request
        console.log('redirecting for 403')
        location.href = 'https://' + vueDomain() + '/?redirected=unauthorized'
        break
      case 500: // server (or axios/browser) thinks something else is wrong.
        console.debug('httpCodeSwitch response status code 500: internal error. ')
        return true;
      default:
        console.debug('httpCodeSwitch response status code: ', code)
        return true
    }
  },
  loggedInRedirect(): void {
    location.href = (location.pathname === '/') ? `https://${fc3MainHostname}` : 'https://' + vueDomain()
  },
  loginRedirect(): void {
    wsClient.close('User redirected by authcheck')
    const redirectTarget = document.URL || 'https://' + vueDomain() + '/'
    // TODO: Cookie “redirect” will be soon rejected because it has the “sameSite” attribute set to “none” or an invalid value, without the “secure” attribute. To know more about the “sameSite“ attribute, read https://developer.mozilla.org/docs/Web/HTTP/Cookies
    Cookies.set('redirect', 'to='+redirectTarget, { domain: 'freecycle.org' })
    resetStore()
    location.href = `https://${fc3MainHostname}/login`
  }
}
